import {
  Card,
  ChoiceChips,
  ColumnChart,
  formatMoney,
  formatNumber,
  KpiGrid,
  KpiTile,
  type KpiDetail,
  type KpiEmphasis,
  type KpiTone,
} from '@platform/design-system';
import { useAuth } from '@platform/auth-client';
import type { TypedClient } from '@platform/openapi';
import { useState } from 'react';
import { StockValueTile } from '../../dashboard/StockValueTile.tsx';
import { LoadFailed } from '../../LoadFailed.tsx';
import { COST_ROLE } from '../product.ts';
import { PeriodChips } from '../../PeriodChips.tsx';
import { DEFAULT_PERIOD, reportPeriodOf, seriesUnitOf, type PeriodPreset } from '../../report-period.ts';
import { usePageContext } from '../../usePageContext.ts';
import {
  PRODUCT_CHART_METRICS,
  productChartPointsOf,
  useProductStatistics,
  type ProductChartMetric,
  type ProductStatisticsReport,
} from './product-statistics.ts';
import styles from './ProductStatistics.module.css';

type Formats = {
  readonly money: (amount: string | null) => string | null;
  readonly count: (units: number) => string;
};

type Detail = readonly [string, string | null];

type Tile = {
  readonly key: string;
  readonly emphasis?: KpiEmphasis;
  readonly value: (report: ProductStatisticsReport, formats: Formats) => string | null;
  readonly details: (report: ProductStatisticsReport, formats: Formats) => readonly Detail[];
  readonly tone?: (report: ProductStatisticsReport) => KpiTone | undefined;
};

const noDetails = (): readonly Detail[] => [];

const TILES: readonly Tile[] = [
  {
    key: 'netGain',
    emphasis: 'hero',
    value: (report, { money }) => money(report.netGain),
    details: noDetails,
  },
  {
    key: 'ordered',
    value: (report, { count }) => count(report.ordered.units),
    details: (report, { money }) => [['amount', money(report.ordered.amount)]],
  },
  {
    key: 'delivered',
    value: (report, { count }) => count(report.delivered.units),
    details: (report, { money }) => [
      ['revenue', money(report.delivered.amount)],
      ['cost', money(report.delivered.cost)],
      ['margin', money(report.delivered.margin)],
    ],
  },
  {
    key: 'manualSales',
    value: (report, { count }) => count(report.manualSales.units),
    details: (report, { money }) => [
      ['amount', money(report.manualSales.amount)],
      ['cost', money(report.manualSales.cost)],
      ['margin', money(report.manualSales.margin)],
    ],
  },
  {
    key: 'carrierReturned',
    value: (report, { count }) => count(report.carrierReturned.units),
    details: (report, { money }) => [['amount', money(report.carrierReturned.amount)]],
  },
  { key: 'receivedBack', value: (report, { count }) => count(report.receivedBack.units), details: noDetails },
  {
    key: 'missing',
    value: (report, { count }) => count(report.missing.units),
    details: (report, { money }) => [['value', money(report.missing.value)]],
    tone: (report) => (report.missing.units > 0 ? 'warning' : undefined),
  },
  {
    key: 'defective',
    value: (report, { count }) => count(report.defective.units),
    details: (report, { money }) => [['value', money(report.defective.value)]],
  },
  {
    key: 'lost',
    value: (report, { count }) => count(report.lost.units),
    details: (report, { money }) => [['value', money(report.lost.value)]],
  },
];

export type ProductFiguresProps = { api: TypedClient; productId: string };

export const ProductFigures = ({ api, productId }: ProductFiguresProps) => {
  const { t, locale, describeError } = usePageContext();
  const [preset, setPreset] = useState<PeriodPreset>(DEFAULT_PERIOD);
  const [openedAt] = useState(() => new Date());
  const [metric, setMetric] = useState<ProductChartMetric>('revenue');
  const unit = seriesUnitOf(preset);
  const statistics = useProductStatistics(api, productId, reportPeriodOf(preset, openedAt), unit);
  const report = statistics.data;
  const formats: Formats = {
    money: (amount) =>
      amount === null || report === undefined
        ? null
        : formatMoney({ amount, currency: report.currency }, locale),
    count: (units) => formatNumber(units, locale),
  };
  const mayReadCosts = useAuth().hasRole(COST_ROLE);
  const costsVisible = report === undefined ? mayReadCosts : report.netGain !== null;
  const shownTiles = TILES.filter((tile) => tile.key !== 'netGain' || costsVisible);
  const metrics = PRODUCT_CHART_METRICS.filter((value) => value !== 'losses' || costsVisible);
  const detailsOf = (tile: Tile): KpiDetail[] =>
    report === undefined
      ? []
      : tile
          .details(report, formats)
          .flatMap(([label, value]) =>
            value === null ? [] : [{ label: t(`productStatistics.${label}`), value }],
          );

  return (
    <div className={styles['figures']}>
      <div className={styles['toolbar']}>
        <PeriodChips value={preset} onChange={setPreset} />
      </div>
      {statistics.isError ? (
        <LoadFailed
          description={describeError(statistics.error)}
          onRetry={() => {
            void statistics.refetch();
          }}
        />
      ) : (
        <>
          <KpiGrid columns={4}>
            {shownTiles.map((tile) => (
              <KpiTile
                key={tile.key}
                label={t(`productStatistics.${tile.key}`)}
                value={report === undefined ? undefined : (tile.value(report, formats) ?? undefined)}
                details={detailsOf(tile)}
                emphasis={tile.emphasis ?? 'standard'}
                tone={report === undefined ? undefined : tile.tone?.(report)}
                hint={tile.key === 'missing' ? t('productStatistics.missingHint') : undefined}
                loading={statistics.isPending}
              />
            ))}
            <StockValueTile api={api} scope={{ productId }} />
          </KpiGrid>
          <Card
            title={t('productStatistics.chartTitle')}
            actions={
              <ChoiceChips
                legend={t('productStatistics.chartTitle')}
                hideLegend
                locale={locale}
                value={metric}
                choices={metrics.map((value) => ({ value, label: t(`productStatistics.metric_${value}`) }))}
                onChange={setMetric}
              />
            }
          >
            <ColumnChart
              label={t('productStatistics.chartLabel', { metric: t(`productStatistics.metric_${metric}`) })}
              points={report === undefined ? [] : productChartPointsOf(report, metric, unit, locale)}
              loading={statistics.isPending}
            />
          </Card>
        </>
      )}
    </div>
  );
};
